import { calculateGradientColor } from '~/_shared/utils/colors/colors.helpers';
import type { HexColor } from '~/_shared/utils/colors/colors.types';
import {
  isBoundaryTerritorySpecial, isBoundaryTerritorySpecialOrCustom,
} from '~/boundary/settings/boundarySettings.helpers';
import { boundaryFillDefaultOpacity } from '~/boundary/settings/defaultBoundaryTerritoryStyles';
import type {
  BoundaryTerritory, BoundaryTerritoryGroupStyle,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';

export const recalculateBoundaryTerritoryColors = (
  boundaryTerritories: ReadonlyArray<BoundaryTerritory>,
  gradientColors: BoundaryTerritoryGroupStyle['gradientColors'],
  keepOpacity = false,
): ReadonlyArray<BoundaryTerritory> => {
  const numericalTerritories = boundaryTerritories.filter(bt => !isBoundaryTerritorySpecialOrCustom(bt));
  const specialTerritories = boundaryTerritories.filter(bt => isBoundaryTerritorySpecialOrCustom(bt));

  const { minimal, low, medium, high } = gradientColors;
  const colorsToUse: NonEmptyArray<HexColor> = [minimal.color, low.color, medium.color, high.color];

  const newNumericalTerritories = numericalTerritories.map((bt, index) => ({
    ...bt,
    style: {
      ...bt.style,
      color: calculateGradientColor({
        position: index,
        totalCount: numericalTerritories.length,
        colors: colorsToUse,
      }),
      opacity: keepOpacity ? bt.style.opacity : boundaryFillDefaultOpacity * 100,
    },
  }));

  return newNumericalTerritories.concat(specialTerritories);
};

// assumes all non-special boundary territories share one opacity, so it is read from the first one
export const getSharedOpacity = (
  boundaryTerritories: ReadonlyArray<BoundaryTerritory>,
): number | undefined => boundaryTerritories
  .find(bt => !isBoundaryTerritorySpecial(bt))
  ?.style.opacity;

export const applySharedOpacity = (
  boundaryTerritories: ReadonlyArray<BoundaryTerritory>,
  opacity: number,
): ReadonlyArray<BoundaryTerritory> => boundaryTerritories.map(bt => (
  isBoundaryTerritorySpecial(bt) ? bt : {
    ...bt,
    style: {
      ...bt.style,
      opacity,
    },
  }
));
